/**
 * Home's tabbed lists: which tabs there are, their counts, where
 * "View all" goes, and what each row's tooltip says, in plain sentences.
 *
 * @module components/home/homeTabs.logic
 */
import type { WorkflowSummary } from "@amoreg/contracts";

import type { RegulatoryUpdate } from "../../state/policies";
import { SEVERITY_LABEL, SEVERITY_ORDER, type FindingItem, type Severity } from "../regulation/finding/findingItem";
import type { HomeFindingGroup } from "./HomeFindings.logic";
import { agoInWords, capitalize, dayMonth, fullDate, longDayMonth, nextRunLabel, pastDay } from "./homeDates.logic";
import { findingDueAt, isFindingOverdue, type HomeGlance } from "./homeGlance.logic";
import type { HomeComingUpItem } from "./homeLogic";

export const HOME_TABS = ["mine", "findings", "due", "updates", "workflows"] as const;
export type HomeTab = (typeof HOME_TABS)[number];

export const HOME_TAB_LABEL: Readonly<Record<HomeTab, string>> = {
  mine: "For you",
  findings: "Findings",
  due: "Scheduled",
  updates: "Updates",
  workflows: "Workflows",
};

export const HOME_TAB_VIEW_ALL = {
  mine: "/$org/compliance/findings",
  findings: "/$org/compliance/findings",
  due: "/$org/compliance",
  updates: "/$org/compliance/regulatory",
  workflows: "/$org/workflows",
} as const satisfies Record<HomeTab, string>;

/** What needs a hand in each tab: assigned, open, scheduled, to review, failing. */
export function homeTabCounts(input: {
  readonly glance: HomeGlance;
  readonly findings: ReadonlyArray<FindingItem>;
  readonly comingUp: ReadonlyArray<HomeComingUpItem>;
}): Record<HomeTab, number> {
  return {
    mine: input.glance.mine.length,
    findings: input.findings.length,
    due: input.comingUp.length,
    updates: input.glance.toReview.length,
    workflows: input.glance.failing.length,
  };
}

const HOME_TAB_COUNT_NOUN: Readonly<Record<HomeTab, string>> = {
  mine: "assigned",
  findings: "open",
  due: "coming up",
  updates: "to review",
  workflows: "failing",
};

/** A tab's count as a screen reader hears it after the label: "1 failing". */
export const homeTabCountLabel = (tab: HomeTab, count: number) => `${count} ${HOME_TAB_COUNT_NOUN[tab]}`;

/** For you when something is assigned to you, else Findings. */
export const defaultHomeTab = (glance: Pick<HomeGlance, "mine">): HomeTab => (glance.mine.length > 0 ? "mine" : "findings");

/** Under a document, the first three findings show; the rest are a link to the document. */
export const FINDING_GROUP_ROWS = 3;
/** The first three documents show; the rest open under "N more". */
export const FINDING_GROUPS_SHOWN = 3;
/** Updates and Workflows list six rows at most. */
export const LIST_ROWS = 6;

type SeverityKey = Severity | "unrated";
const SEVERITY_KEYS: ReadonlyArray<SeverityKey> = [...SEVERITY_ORDER, "unrated"];
const SEVERITY_NAME: Readonly<Record<SeverityKey, string>> = { ...SEVERITY_LABEL, unrated: "Unrated" };

/** A document's count tooltip: "1 critical, 2 medium". */
export const findingGroupBreakdown = (group: Pick<HomeFindingGroup, "counts">) =>
  SEVERITY_KEYS.filter((key) => group.counts[key] > 0)
    .map((key) => `${group.counts[key]} ${SEVERITY_NAME[key].toLowerCase()}`)
    .join(", ");


/** For you's date column: the due day, or "No date". */
export function findingDueColumn(item: Pick<FindingItem, "dueDate">, now: number): string {
  const at = findingDueAt(item);
  return Number.isFinite(at) ? dayMonth(at, now) : "No date";
}

/**
 * A finding row's tooltip, one fact per line. Under a document: where it comes from and when it was raised.
 * In For you, which also says its severity and document and when it is due.
 */
export function findingRowTooltip(
  item: Pick<FindingItem, "severity" | "documentName" | "policyId" | "basis" | "raisedAt" | "dueDate">,
  now: number,
  withDue: boolean,
): string {
  const at = findingDueAt(item);
  const raised = `Raised ${agoInWords(new Date(item.raisedAt).toISOString(), now)}`;
  const due = Number.isFinite(at) ? `${isFindingOverdue(item, now) ? "Overdue since" : "Due"} ${longDayMonth(at, now)}` : "No due date";
  // "Isle of Man · Higher-risk jurisdictions list" reads as "Higher-risk jurisdictions list (Isle of Man)".
  const [where, what] = item.basis.label.split(" · ");
  const source = item.policyId === null ? null : what ? `${what} (${where})` : item.basis.label;
  const severity = `${item.severity ? `${SEVERITY_NAME[item.severity]} severity` : "Unrated"} in ${item.documentName}`;
  const lines = withDue ? [severity, source, `${raised}. ${due}`] : [source, raised];
  return lines.filter(Boolean).join("\n");
}

/** "Annual review of Sanctions Screening Policy. Due Thursday 22 October 2026". */
export function scheduledRowTooltip(item: HomeComingUpItem): string {
  const what = item.kind === "review" ? `${item.title} of ${item.context}` : item.context ? `${item.title} (${item.context})` : item.title;
  const when = item.kind === "review" ? (item.overdue ? "Overdue since" : "Due") : item.detail;
  return `${what}. ${when} ${fullDate(Date.parse(item.at))}`;
}

/** The title, its jurisdiction, and when it was spotted. */
export function updateRowTooltip(update: Pick<RegulatoryUpdate, "title" | "jurisdictionName" | "detectedAt">, now: number): string {
  const spotted = Date.parse(update.detectedAt);
  const day = pastDay(spotted, now);
  const spottedLabel = `Spotted ${day === dayMonth(spotted, now) ? `on ${longDayMonth(spotted, now)}` : day.toLowerCase()}`;
  return `${update.title}\n${update.jurisdictionName}\n${spottedLabel}`;
}

/**
 * Updates and Workflows: one list while every item is on one side, else the
 * ones that need a hand (the tab's count) over the rest. Every item that needs
 * a hand shows, so the list always holds what the count counts; the rest show
 * six at most.
 */
export type SplitList<T> =
  | { readonly kind: "flat"; readonly items: ReadonlyArray<T> }
  | {
      readonly kind: "split";
      readonly needsHand: ReadonlyArray<T>;
      /** The first six of the rest; `restCount` counts them all. */
      readonly rest: ReadonlyArray<T>;
      readonly restCount: number;
    };

function splitList<T>(items: ReadonlyArray<T>, needsHand: ReadonlyArray<T>): SplitList<T> {
  const rest = items.filter((item) => !needsHand.includes(item));
  if (needsHand.length === 0) return { kind: "flat", items: items.slice(0, LIST_ROWS) };
  if (rest.length === 0) return { kind: "flat", items: needsHand };
  return { kind: "split", needsHand, rest: rest.slice(0, LIST_ROWS), restCount: rest.length };
}

/** Newest first: To review over Reviewed. */
export const updateList = (glance: Pick<HomeGlance, "updates" | "toReview">): SplitList<RegulatoryUpdate> =>
  splitList(glance.updates, glance.toReview);

/** How a workflow's last run went; red only when it failed. */
export type WorkflowState = "failed" | "running" | "waiting" | "succeeded" | "cancelled" | "never";

export function workflowState(workflow: Pick<WorkflowSummary, "lastRun" | "schedule">): WorkflowState {
  const status = workflow.lastRun?.status;
  if (status === "failed" || workflow.schedule?.lastError) return "failed";
  if (status === undefined) return "never";
  return status === "queued" ? "running" : status;
}

/** The most recently run or changed first: Failing over the rest. */
export function workflowList(glance: Pick<HomeGlance, "workflows" | "failing">): SplitList<WorkflowSummary> {
  const recent = glance.workflows.toSorted(
    (a, b) => Date.parse(b.lastRun?.createdAt ?? b.updatedAt) - Date.parse(a.lastRun?.createdAt ?? a.updatedAt),
  );
  return splitList(recent, recent.filter((workflow) => glance.failing.includes(workflow)));
}

/** A workflow row's tooltip (its last run, then its schedule) and its date column (next run, else last). */
export function workflowRowText(
  workflow: Pick<WorkflowSummary, "lastRun" | "schedule">,
  now: number,
): { readonly state: WorkflowState; readonly tooltip: string; readonly trailing: string | null } {
  const run = workflow.lastRun;
  const state = workflowState(workflow);
  const ago = run ? agoInWords(run.finishedAt ?? run.createdAt, now) : null;
  const status = {
    failed: run?.status === "failed" ? `Failed ${ago}` : "The last scheduled run could not start",
    running: "Running now",
    waiting: "Waiting for a decision",
    succeeded: `Last ran ${ago}`,
    cancelled: `Cancelled ${ago}`,
    never: "Never run",
  }[state];
  const nextRunAt = workflow.schedule?.nextRunAt ? Date.parse(workflow.schedule.nextRunAt) : null;
  return {
    state,
    tooltip: [status, workflow.schedule?.description].filter(Boolean).join("\n"),
    trailing: nextRunAt !== null ? nextRunLabel(nextRunAt, now) : ago ? capitalize(ago) : null,
  };
}
